import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useOrgContext } from "../context/OrgContext";
import { useLocale } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

/**
 * Organization picker interstitial page.
 * - Single org: auto-redirect to `/:orgSlug/`
 * - Multiple orgs: shows a selection grid (ADMIN sees an "Admin Console" shortcut)
 * - Zero orgs + ADMIN: auto-redirect to /admin/organizations
 * - Zero orgs + ORG_ADMIN: informational message
 */
export function OrgPickerPage() {
  const navigate = useNavigate();
  const { managedOrgs, isLoading } = useOrgContext();
  const { t } = useLocale();
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "ADMIN";

  // Auto-redirect:
  // - single org → enter that org
  // - platform ADMIN with zero orgs → admin console (so a fresh tenant isn't a dead end)
  useEffect(() => {
    if (isLoading) return;
    if (managedOrgs.length === 1) {
      navigate(`/${managedOrgs[0].slug}`, { replace: true });
      return;
    }
    if (managedOrgs.length === 0 && isPlatformAdmin) {
      navigate("/admin/organizations", { replace: true });
    }
  }, [isLoading, managedOrgs, isPlatformAdmin, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (managedOrgs.length === 0) {
    if (isPlatformAdmin) {
      // Redirect is pending via the effect above — show spinner while it lands.
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">{t("orgPicker.noOrgs")}</h1>
          <p className="mt-2 text-muted-foreground">
            {t("orgPicker.noAccess")}
          </p>
        </div>
      </div>
    );
  }

  // Single org auto-redirects via effect above — show loading briefly
  if (managedOrgs.length === 1) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-start justify-center bg-background pt-16 sm:items-center sm:pt-0">
      <div className="w-full max-w-md px-6 py-8">
        <div className="text-center mb-8">
          <span className="text-3xl font-bold text-primary">Kommit</span>
          <h1 className="mt-4 text-xl font-semibold text-foreground">
            {t("orgPicker.selectOrg")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("orgPicker.chooseOrg")}
          </p>
        </div>

        <div className="space-y-3">
          {managedOrgs.map((org) => (
            <button
              key={org._id}
              type="button"
              onClick={() => navigate(`/${org.slug}`)}
              className="flex w-full items-center gap-4 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted"
            >
              {org.picture ? (
                <img
                  src={org.picture}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {org.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">
                  {org.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {org.memberCount === 1 ? t("orgPicker.memberCount", { count: org.memberCount }) : t("orgPicker.memberCountPlural", { count: org.memberCount })}
                </div>
              </div>
            </button>
          ))}
        </div>

        {isPlatformAdmin && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate("/admin/organizations")}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {t("orgPicker.adminConsole")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
